import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  NavDropdown,
  Image,
  Placeholder,
  Offcanvas,
  Button,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faBasketShopping,
  faBoxesStacked,
  faBoxOpen,
  faCreditCard,
  faEnvelope,
  faGaugeHigh,
  faHardDrive,
  faToolbox,
  faRightFromBracket,
  faShop,
  faSliders,
  faUser,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

import { useAuth } from "../../contexts/AuthContext";
import Utils from "../../configs/Utils";

import "./style.scss";

const PageHeader = () => {
  const { currentUser, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  const primaryNavigationLinks = [
    { to: "/", label: "Dashboard", icon: faGaugeHigh },
    { to: "/orders", label: "Orders", icon: faBasketShopping },
    { to: "/mail-templates", label: "Templates", icon: faEnvelope },
    { to: "/products", label: "Products", icon: faBoxOpen },
    { to: "/tool-support", label: "Tools", icon: faToolbox },
  ];

  const compactNavigationLinks = [
    { to: "/stores", label: "Stores", icon: faShop },
    { to: "/supplier-stock", label: "Stock", icon: faBoxesStacked },
    { to: "/gateways", label: "Gateways", icon: faCreditCard },
  ];

  const quickActions = [
    { label: "Profile", icon: faUser, onClick: () => navigate("/profile") },
    { label: "Workspace", icon: faUsers, onClick: () => navigate("/workspace") },
    { label: "Settings", icon: faSliders, onClick: () => navigate("/settings") },
  ];

  const isLinkActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }

    return location.pathname.startsWith(path);
  };

  const isCompactDropdownActive = compactNavigationLinks.some((link) => isLinkActive(link.to));

  const logoutClickHandle = async (e) => {
    e.preventDefault();
    try {
      await logout();
      navigate("/");
    } catch (err) {
      console.log("request Auth error: ", err);
    }
  };

  const handleShow = () => {
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
  };

  return (
    <>
      {isAuthenticated && (
        <>
          <header className="main-header">
            <nav className="header-shell container" aria-label="Primary navigation">
              <div className="header-brand-block">
                <Button
                  variant="light"
                  className="mobile-menu-btn d-inline-flex d-lg-none"
                  onClick={handleShow}
                  aria-label="Open navigation"
                >
                  <FontAwesomeIcon icon={faBars} />
                </Button>

                <Link className="brand-link" to="/">
                  <span className="brand-logo-wrap">
                    <Image
                      src={Utils.getPublicImage("/assets/images/logo-main.png")}
                      className="brand-logo"
                      alt="Thadotech Gateway Manager"
                    />
                  </span>
                </Link>

                <div className="header-nav-panel d-none d-lg-flex">
                  <ul className="main-nav-links">
                    {primaryNavigationLinks.map((link) => (
                      <li className="nav-item" key={link.to}>
                        <Link
                          className={`nav-link${isLinkActive(link.to) ? " active" : ""}`}
                          to={link.to}
                        >
                          <FontAwesomeIcon icon={link.icon} className="me-2" />
                          {link.label}
                        </Link>
                      </li>
                    ))}
                    <li className="nav-item nav-item-dropdown">
                      <NavDropdown
                        id="main-links-dropdown"
                        className={`main-nav-dropdown${isCompactDropdownActive ? " active" : ""}`}
                        title={
                          <>
                            <FontAwesomeIcon icon={faHardDrive} className="me-2" />
                            More
                          </>
                        }
                      >
                        {compactNavigationLinks.map((link) => (
                          <NavDropdown.Item as={Link} to={link.to} key={link.to}>
                            <FontAwesomeIcon icon={link.icon} className="me-2" />
                            {link.label}
                          </NavDropdown.Item>
                        ))}
                      </NavDropdown>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="header-actions">
                <NavDropdown
                  id="account-dropdown"
                  className="account-dropdown"
                  align="end"
                  title={
                    <div className="account-trigger">
                      <Image
                        src={Utils.getPublicImage("/assets/images/profile-icon-new.png")}
                        roundedCircle
                        width={40}
                        className="account-trigger-avatar"
                      />
                      <div className="account-trigger-copy d-none d-md-block">
                        <strong>
                          {currentUser?._id ? (
                            currentUser.fullName || currentUser.email
                          ) : (
                            <Placeholder animation="glow">
                              <Placeholder style={{ width: "110px", display: "inline-block" }} />
                            </Placeholder>
                          )}
                        </strong>
                        <small>{currentUser?.admin ? "Administrator" : "Team member"}</small>
                      </div>
                    </div>
                  }
                >
                  {quickActions.map((action) => (
                    <NavDropdown.Item key={action.label} onClick={action.onClick}>
                      <FontAwesomeIcon icon={action.icon} className="me-2" />
                      {action.label}
                    </NavDropdown.Item>
                  ))}
                  <NavDropdown.Divider />
                  <NavDropdown.Item as="button" type="button" onClick={logoutClickHandle} className="text-danger">
                    <FontAwesomeIcon icon={faRightFromBracket} className="me-2" />
                    Logout
                  </NavDropdown.Item>
                </NavDropdown>
              </div>
            </nav>
          </header>

          <Offcanvas show={show} onHide={handleClose} placement="start" className="mobile-nav-canvas">
            <Offcanvas.Header closeButton>
              <Offcanvas.Title>
                <div className="mobile-nav-brand">
                  <Image
                    src={Utils.getPublicImage("/assets/images/logo-main.png")}
                    className="mobile-nav-logo"
                    alt="Thadotech Gateway Manager"
                  />
                  <span>Control center</span>
                </div>
              </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <div className="mobile-account-card">
                <Image
                  src={Utils.getPublicImage("/assets/images/profile-icon-new.png")}
                  roundedCircle
                  width={48}
                  className="mobile-account-avatar"
                />
                <div>
                  <strong>{currentUser?.fullName || currentUser?.email}</strong>
                  <small>{currentUser?.admin ? "Administrator" : "Team member"}</small>
                </div>
              </div>

              <ul className="nav nav-pills flex-column mb-4">
                {primaryNavigationLinks.map((link) => (
                  <li className="nav-item" key={link.to}>
                    <Link
                      className={`nav-link${isLinkActive(link.to) ? " active" : ""}`}
                      to={link.to}
                      onClick={handleClose}
                    >
                      <FontAwesomeIcon icon={link.icon} size="lg" className="bi pe-none me-2" />
                      {link.label}
                    </Link>
                  </li>
                ))}
                <li className="mobile-nav-section-label" aria-hidden="true">
                  More
                </li>
                {compactNavigationLinks.map((link) => (
                  <li className="nav-item" key={link.to}>
                    <Link
                      className={`nav-link${isLinkActive(link.to) ? " active" : ""}`}
                      to={link.to}
                      onClick={handleClose}
                    >
                      <FontAwesomeIcon icon={link.icon} size="lg" className="bi pe-none me-2" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="mobile-nav-utility">
                {quickActions.map((action) => (
                  <Button
                    key={action.label}
                    variant="light"
                    className="w-100 justify-content-start"
                    onClick={() => {
                      handleClose();
                      action.onClick();
                    }}
                  >
                    <FontAwesomeIcon icon={action.icon} className="me-2" />
                    {action.label}
                  </Button>
                ))}

                <Button variant="outline-danger" className="w-100 justify-content-start" onClick={logoutClickHandle}>
                  <FontAwesomeIcon icon={faRightFromBracket} className="me-2" />
                  Logout
                </Button>
              </div>
            </Offcanvas.Body>
          </Offcanvas>
        </>
      )}
    </>
  );
};

export default PageHeader;
